import type { Point, RoomSeparator, Space, Wall } from '../types';

// ── Detection des pieces (modele Revit) ────────────────────
// Les elements qui delimitent les pieces (murs, cloisons, lignes de separation) forment un graphe plan
// de leurs axes. Chaque face fermee du graphe est un espace ; une piece est l'espace qui contient son
// point de placement. Le contour de la piece est pris au nu interieur des murs (plaque a plaque).
// Coordonnees : px du plan.

export interface BoundarySegment {
  a: Point;
  b: Point;
  /** Demi-epaisseur (px) : decalage du nu interieur par rapport a l'axe (0 pour une ligne de separation) */
  half: number;
}

export interface RoomFace {
  /** Cle stable (noeuds du contour) */
  key: string;
  /** Contour sur les axes, oriente pour que l'interieur soit a gauche de chaque arete */
  outline: Point[];
  /** Contour au nu interieur des murs */
  inner: Point[];
  /** Surface au nu interieur (px²) */
  area: number;
  /** Perimetre au nu interieur (px) */
  perimeter: number;
}

export type SpaceState = 'ok' | 'non_fermee' | 'redondante';

const TOL = 2;

const sub = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
const cross = (a: Point, b: Point) => a.x * b.y - a.y * b.x;
const dot = (a: Point, b: Point) => a.x * b.x + a.y * b.y;
const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

export function signedArea(poly: Point[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}

export function pointInPolygon(p: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Segments delimitants d'un etage : murs `roomBounding` + lignes de separation */
export function boundarySegments(walls: Wall[], separators: RoomSeparator[], cmToPx: (cm: number) => number): BoundarySegment[] {
  return [
    ...walls.filter(w => w.roomBounding !== false).map(w => ({ a: w.start, b: w.end, half: cmToPx(w.thickness) / 2 })),
    ...separators.map(r => ({ a: r.start, b: r.end, half: 0 })),
  ].filter(s => dist(s.a, s.b) > TOL);
}

/**
 * Faces fermees du graphe des segments delimitants.
 * 1. Les extremites posees dans l'epaisseur d'un segment (cloison en T) sont ramenees sur son axe.
 * 2. Les segments sont coupes a leurs intersections et jonctions ; les bouts libres sont elagues.
 * 3. Parcours des demi-aretes : chaque cycle d'aire positive est une face bornee.
 */
export function buildFaces(input: BoundarySegment[]): RoomFace[] {
  // 1. Extremites en T ramenees sur l'axe de l'hote
  const segs = input.map(s => ({ ...s, a: { ...s.a }, b: { ...s.b } }));
  const links: BoundarySegment[] = [];
  for (const s of segs) {
    for (const end of ['a', 'b'] as const) {
      const p = s[end];
      for (const h of segs) {
        if (h === s) continue;
        const ab = sub(h.b, h.a);
        const L = Math.hypot(ab.x, ab.y);
        const along = dot(sub(p, h.a), ab) / L;
        // Dans l'epaisseur de l'hote, y compris au droit de son extremite (cloison arretee contre l'angle d'un
        // mur) : ramenee sur l'axe, au bout de l'hote dans ce dernier cas
        if (along < -h.half - TOL || along > L + h.half + TOL) continue;
        if ((along <= TOL || along >= L - TOL) && h.half <= 0) continue;
        const d = Math.abs(cross(ab, sub(p, h.a))) / L;
        if (d > Math.max(TOL, h.half + TOL)) continue;
        const t = Math.max(0, Math.min(L, along));
        const q = { x: h.a.x + (ab.x * t) / L, y: h.a.y + (ab.y * t) / L };
        // Au droit de l'extremite : la cloison garde sa position, un raccord la relie au bout de l'axe de l'hote
        if (t !== along) links.push({ a: { ...p }, b: q, half: 0 });
        else s[end] = q;
        break;
      }
    }
  }
  segs.push(...links.map(l => ({ ...l })));

  // 2. Noeuds (fusion a TOL pres) et decoupe des segments
  const nodes: Point[] = [];
  const nodeOf = (p: Point): number => {
    for (let i = 0; i < nodes.length; i++) if (dist(nodes[i], p) < TOL) return i;
    nodes.push(p);
    return nodes.length - 1;
  };
  const edges = new Map<string, { u: number; v: number; half: number }>();
  for (const s of segs) {
    const ab = sub(s.b, s.a);
    const L = Math.hypot(ab.x, ab.y);
    const ts = [0, 1];
    for (const o of segs) {
      if (o === s) continue;
      // Intersection propre ou contact d'extremite
      const cd = sub(o.b, o.a);
      const den = cross(ab, cd);
      if (Math.abs(den) > 1e-9) {
        const t = cross(sub(o.a, s.a), cd) / den;
        const u = cross(sub(o.a, s.a), ab) / den;
        const uTol = TOL / Math.hypot(cd.x, cd.y);
        if (t > 0 && t < 1 && u > -uTol && u < 1 + uTol) ts.push(t);
      }
      // Extremites de l'autre segment posees sur celui-ci (colineaires compris)
      for (const p of [o.a, o.b]) {
        const along = dot(sub(p, s.a), ab) / L;
        if (along > TOL && along < L - TOL && Math.abs(cross(ab, sub(p, s.a))) / L < TOL) ts.push(along / L);
      }
    }
    ts.sort((p, q) => p - q);
    let prev = nodeOf(s.a);
    for (let k = 1; k < ts.length; k++) {
      const n = nodeOf({ x: s.a.x + ab.x * ts[k], y: s.a.y + ab.y * ts[k] });
      if (n !== prev) {
        const key = prev < n ? `${prev}-${n}` : `${n}-${prev}`;
        const e = edges.get(key);
        if (!e) edges.set(key, { u: prev, v: n, half: s.half });
        else e.half = Math.max(e.half, s.half);
      }
      prev = n;
    }
  }

  // Elagage des bouts libres (cloison qui ne ferme rien)
  const adj = new Map<number, Set<number>>();
  const link = (a: number, b: number) => { if (!adj.has(a)) adj.set(a, new Set()); adj.get(a)!.add(b); };
  for (const e of edges.values()) { link(e.u, e.v); link(e.v, e.u); }
  let pruned = true;
  while (pruned) {
    pruned = false;
    for (const [n, nb] of adj) {
      if (nb.size > 1) continue;
      for (const m of nb) adj.get(m)?.delete(n);
      adj.delete(n);
      pruned = true;
    }
  }
  const halfOf = (a: number, b: number) => edges.get(a < b ? `${a}-${b}` : `${b}-${a}`)?.half ?? 0;

  // 3. Demi-aretes : a chaque noeud, voisins tries par angle
  const sorted = new Map<number, number[]>();
  for (const [n, nb] of adj) {
    const p = nodes[n];
    sorted.set(n, [...nb].sort((a, b) =>
      Math.atan2(nodes[a].y - p.y, nodes[a].x - p.x) - Math.atan2(nodes[b].y - p.y, nodes[b].x - p.x)));
  }
  const visited = new Set<string>();
  const faces: RoomFace[] = [];
  for (const [u, nb] of adj) {
    for (const v of nb) {
      if (visited.has(`${u}>${v}`)) continue;
      const cycle: number[] = [];
      let a = u, b = v, guard = 0;
      while (!visited.has(`${a}>${b}`) && guard++ < 10000) {
        visited.add(`${a}>${b}`);
        cycle.push(a);
        const around = sorted.get(b)!;
        const i = around.indexOf(a);
        const next = around[(i - 1 + around.length) % around.length];
        a = b; b = next;
      }
      const outline = cycle.map(n => nodes[n]);
      if (outline.length < 3 || signedArea(outline) <= 1e-6) continue; // face exterieure
      const halves = cycle.map((n, i) => halfOf(n, cycle[(i + 1) % cycle.length]));
      const inner = insetPolygon(outline, halves);
      faces.push({
        key: [...cycle].sort((p, q) => p - q).join(','),
        outline,
        inner,
        area: Math.abs(signedArea(inner)),
        perimeter: inner.reduce((s, p, i) => s + dist(p, inner[(i + 1) % inner.length]), 0),
      });
    }
  }
  return faces;
}

/** Decale chaque arete vers l'interieur (a gauche) de sa propre distance ; sommets = intersections */
export function insetPolygon(poly: Point[], offsets: number[]): Point[] {
  const n = poly.length;
  const lines = poly.map((a, i) => {
    const b = poly[(i + 1) % n];
    const d = sub(b, a);
    const L = Math.hypot(d.x, d.y) || 1;
    const nrm = { x: -d.y / L, y: d.x / L };
    return { p: { x: a.x + nrm.x * offsets[i], y: a.y + nrm.y * offsets[i] }, d, nrm };
  });
  return poly.map((v, i) => {
    const l1 = lines[(i - 1 + n) % n], l2 = lines[i];
    const den = cross(l1.d, l2.d);
    if (Math.abs(den) < 1e-9) return { x: v.x + l2.nrm.x * offsets[i], y: v.y + l2.nrm.y * offsets[i] };
    const t = cross(sub(l2.p, l1.p), l2.d) / den;
    return { x: l1.p.x + l1.d.x * t, y: l1.p.y + l1.d.y * t };
  });
}

/** Plus petite face qui contient le point (null si le point n'est dans aucun espace ferme) */
export function faceAt(faces: RoomFace[], p: Point): RoomFace | null {
  let best: RoomFace | null = null;
  for (const f of faces) {
    if (!pointInPolygon(p, f.outline)) continue;
    if (!best || Math.abs(signedArea(f.outline)) < Math.abs(signedArea(best.outline))) best = f;
  }
  return best;
}

/** Point bien a l'interieur d'un polygone (pour placer une piece / une etiquette) */
export function interiorPoint(poly: Point[]): Point {
  const xs = poly.map(p => p.x), ys = poly.map(p => p.y);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const c = { x: poly.reduce((s, p) => s + p.x, 0) / poly.length, y: poly.reduce((s, p) => s + p.y, 0) / poly.length };
  const clearance = (p: Point) => {
    let m = Infinity;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i], b = poly[(i + 1) % poly.length];
      const ab = sub(b, a);
      const L2 = dot(ab, ab) || 1;
      const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / L2));
      m = Math.min(m, dist(p, { x: a.x + ab.x * t, y: a.y + ab.y * t }));
    }
    return m;
  };
  let best = pointInPolygon(c, poly) ? c : null;
  let bestC = best ? clearance(best) : -1;
  const N = 12;
  for (let i = 1; i < N; i++) {
    for (let j = 1; j < N; j++) {
      const p = { x: x0 + ((x1 - x0) * i) / N, y: y0 + ((y1 - y0) * j) / N };
      if (!pointInPolygon(p, poly)) continue;
      const cl = clearance(p);
      if (cl > bestC * 1.5) { best = p; bestC = cl; }
    }
  }
  return best ?? c;
}

export interface ResolvedSpace {
  space: Space;
  face: RoomFace | null;
  state: SpaceState;
}

/** Associe chaque piece a son espace ferme ; signale les pieces non fermees et redondantes */
export function resolveSpaces(spaces: Space[], faces: RoomFace[]): ResolvedSpace[] {
  const taken = new Set<string>();
  return spaces.map(space => {
    const face = faceAt(faces, space.point);
    if (!face) return { space, face: null, state: 'non_fermee' as const };
    if (taken.has(face.key)) return { space, face, state: 'redondante' as const };
    taken.add(face.key);
    return { space, face, state: 'ok' as const };
  });
}

/** Espaces fermes sans piece : points de placement pour « Placer automatiquement » */
export function unplacedFaces(spaces: Space[], faces: RoomFace[]): RoomFace[] {
  const used = new Set(spaces.map(s => faceAt(faces, s.point)?.key).filter(Boolean));
  return faces.filter(f => !used.has(f.key));
}

/**
 * Dimensions d'une piece a l'endroit `at` (dans la piece) : etendue face a face de la ligne horizontale et de
 * la ligne verticale qui passent par `at` (bornes le long de chaque ligne). Juste aussi pour une piece en L.
 */
export function roomSpansAt(inner: Point[], at: Point): { h: [number, number] | null; v: [number, number] | null } {
  const xs: number[] = [], ys: number[] = [];
  for (let i = 0; i < inner.length; i++) {
    const a = inner[i], b = inner[(i + 1) % inner.length];
    if ((a.y > at.y) !== (b.y > at.y)) xs.push(a.x + ((at.y - a.y) * (b.x - a.x)) / (b.y - a.y));
    if ((a.x > at.x) !== (b.x > at.x)) ys.push(a.y + ((at.x - a.x) * (b.y - a.y)) / (b.x - a.x));
  }
  const around = (vals: number[], c: number): [number, number] | null => {
    const lo = Math.max(...vals.filter(v => v <= c)), hi = Math.min(...vals.filter(v => v >= c));
    return Number.isFinite(lo) && Number.isFinite(hi) && hi - lo > 1e-6 ? [lo, hi] : null;
  };
  return { h: around(xs, at.x), v: around(ys, at.y) };
}
